import type { Emotion, EmotionMode } from '../types/emotion';
import type { ChatPhase } from '../types/chat';

export interface BrainInput {
  t: number;
  dt: number;
  /** Pointer in normalized screen space (-1..1) */
  pointer: { x: number; y: number };
  pointerMovedAt: number;
  pointerInWindow: boolean;
  /** Smoothed pointer speed in screen-widths per second */
  pointerSpeed: number;
  hovering: boolean;
  scrollVelocity: number;
  mode: EmotionMode;
  /** Visitor is currently dragging the robot */
  dragging: boolean;
  /** Drag velocity in world units per second */
  dragVelocity: { x: number; y: number };
  /** Play state from the roamer: zipping around or poking at a spot */
  playing: 'dash' | 'explore' | null;
  /** Direction of travel while playing (-1 left, 1 right) */
  playDir: number;
  /** Conversation state from the chat bubbles */
  chat: { phase: ChatPhase; emotion: Emotion };
}

export interface BrainOutput {
  emotion: Emotion;
  gaze: { x: number; y: number };
  head: { yaw: number; pitch: number; roll: number };
  body: { x: number; y: number; z: number };
  bobAmp: number;
  bobSpeed: number;
  /** One-shot upward impulse (a little hop) */
  hop: number;
  /** 1 = side plates tucked into the body (ball shape), 0 = extended */
  armsClosed: number;
}

const rand = (min: number, max: number) => min + Math.random() * (max - min);

/** Where the eyes go during a conversation */
function chatGaze(phase: ChatPhase, t: number, active: boolean, pointer: { x: number; y: number }) {
  switch (phase) {
    case 'thinking':
      // Looks up and off to the side, eyes wandering while it thinks
      return { x: 0.35 + Math.sin(t * 1.3) * 0.25, y: 0.55 };
    case 'speaking':
      // Talks to you — mostly straight at the viewer, small glances toward the bubbles
      return { x: 0.12 + Math.sin(t * 0.7) * 0.12, y: 0.02 };
    case 'listening':
      // Waits for your answer: follows your cursor, otherwise eyes the reply options
      return active ? { x: pointer.x, y: pointer.y } : { x: 0.45, y: -0.25 };
    default:
      return active ? { x: pointer.x * 0.5, y: pointer.y * 0.5 } : { x: 0.1, y: 0 };
  }
}
const pick = <T,>(items: T[]): T => items[Math.floor(Math.random() * items.length)];

/**
 * Small behavior model: idles, looks around, tilts its head, wanders,
 * follows the visitor, and reacts to hovering, clicks, shaking and inactivity.
 */
export class RobotBrain {
  private reaction: Emotion | null = null;
  private reactionUntil = 0;
  private queued: { emotion: Emotion; at: number; duration: number } | null = null;

  private mood: Emotion = 'neutral';
  private nextMoodAt = 6;

  private idleLook = { x: 0, y: 0 };
  private nextGlanceAt = 1;

  private tilt = 0;
  private tiltUntil = 0;
  private nextTiltAt = 4;

  private wander = { x: 0, y: 0 };
  private nextWanderAt = 3;

  private wasSleeping = false;
  private wasHovering = false;
  private wasInWindow = true;
  private shakeTime = 0;
  private pendingHop = 0;

  private dragStart = 0;
  private nextTuckAt = 9;
  private tuckUntil = 0;

  /** Visitor grabbed the robot. */
  startDrag(t: number) {
    this.dragStart = t;
  }

  /** Visitor let go after moving the robot. */
  endDrag(t: number) {
    this.react('surprised', 0.3, t, true);
    this.queued = { emotion: 'curious', at: t + 0.3, duration: 1.4 };
  }

  private react(emotion: Emotion, duration: number, t: number, force = false) {
    if (!force && this.reaction && t < this.reactionUntil) return;
    this.reaction = emotion;
    this.reactionUntil = t + duration;
  }

  /** Force an expression for a moment (e.g. while flying to/from the dock). */
  express(emotion: Emotion, duration: number, t: number) {
    this.react(emotion, duration, t, true);
  }

  /** Visitor clicked/tapped. */
  click(onRobot: boolean, t: number, pointer: { x: number; y: number }) {
    if (onRobot) {
      this.react('surprised', 0.35, t, true);
      this.queued = { emotion: 'happy', at: t + 0.35, duration: 2.4 };
    } else {
      this.idleLook = { x: pointer.x, y: pointer.y };
      this.nextGlanceAt = t + 1.5;
      this.react('curious', 1.4, t, true);
    }
  }

  update(input: BrainInput): BrainOutput {
    const { t, pointer, pointerMovedAt, pointerInWindow, pointerSpeed, hovering, scrollVelocity } = input;
    const sinceMove = t - pointerMovedAt;
    const active = pointerInWindow && sinceMove < 2.5;
    const chatPhase = input.chat.phase;
    const chatting = chatPhase !== 'idle';
    // Never dozes off mid-conversation — the visitor is reading
    const sleeping = !chatting && ((pointerInWindow && sinceMove > 12) || (!pointerInWindow && sinceMove > 8));

    // --- Reactions to visitor behavior ---
    if (this.wasSleeping && !sleeping) this.react('surprised', 0.8, t, true);
    this.wasSleeping = sleeping;

    if (hovering && !this.wasHovering) {
      this.react('surprised', 0.25, t, true);
      this.queued = { emotion: 'happy', at: t + 0.25, duration: 0.1 };
    }
    this.wasHovering = hovering;

    if (!pointerInWindow && this.wasInWindow) {
      // Look toward where the visitor left
      this.idleLook = { x: Math.sign(pointer.x || 1) * 0.9, y: pointer.y * 0.5 };
      this.nextGlanceAt = t + 1.8;
      this.react('curious', 1.8, t, true);
    }
    this.wasInWindow = pointerInWindow;

    if (pointerSpeed > 4.5) this.shakeTime += input.dt;
    else this.shakeTime = Math.max(0, this.shakeTime - input.dt * 2);
    if (this.shakeTime > 1.3) {
      this.react('angry', 2.2, t, true);
      this.shakeTime = 0;
    } else if (pointerSpeed > 7) {
      this.react('surprised', 0.7, t);
    }

    if (scrollVelocity > 0.5) this.react('surprised', 0.9, t);

    if (this.queued && t >= this.queued.at) {
      this.react(this.queued.emotion, this.queued.duration, t, true);
      this.queued = null;
    }

    if (t > this.nextMoodAt) {
      this.mood = pick<Emotion>(['neutral', 'neutral', 'curious', 'happy', 'neutral', 'sad']);
      this.nextMoodAt = t + rand(5, 10);
    }

    const reacting = this.reaction !== null && t < this.reactionUntil;
    let emotion: Emotion = reacting
      ? (this.reaction as Emotion)
      : hovering
        ? 'happy'
        : sleeping
          ? 'sleepy'
          : !pointerInWindow
            ? 'curious'
            : this.mood;
    if (!reacting && !hovering && !sleeping && !input.dragging && input.playing) {
      emotion = input.playing === 'dash' ? 'happy' : 'curious';
    }
    // In conversation the face follows the chat: puzzled while thinking, the line's mood while talking
    if (chatting && !input.dragging && !reacting) {
      emotion = chatPhase === 'thinking' ? 'curious' : input.chat.emotion;
    }
    if (input.dragging) emotion = t - this.dragStart < 0.45 ? 'surprised' : 'happy';
    if (input.mode !== 'auto') emotion = input.mode;

    // --- Where to look ---
    if (t > this.nextGlanceAt) {
      const range = pointerInWindow ? 0.7 : 1;
      this.idleLook = { x: rand(-range, range), y: rand(-0.5, 0.5) };
      this.nextGlanceAt = t + rand(0.8, 3.2);
    }
    const clamp1 = (v: number) => Math.max(-1, Math.min(1, v));
    const gaze = input.dragging
      ? { x: clamp1(input.dragVelocity.x / 3), y: clamp1(input.dragVelocity.y / 3) }
      : chatting && !hovering && input.playing !== 'dash'
      ? chatGaze(chatPhase, t, active, pointer)
      : input.playing === 'dash' && !active && !hovering
      ? { x: input.playDir * 0.9, y: 0.15 }
      : sleeping
      ? { x: this.idleLook.x * 0.2, y: -0.7 }
      : active || hovering
        ? { x: pointer.x, y: pointer.y }
        : this.idleLook;

    // --- Head tilt (curious "hmm?") ---
    if (t > this.nextTiltAt) {
      this.tilt = (Math.random() > 0.5 ? 1 : -1) * rand(0.1, 0.24);
      this.tiltUntil = t + rand(1.2, 2.6);
      this.nextTiltAt = t + rand(5, 11);
    }
    let roll = t < this.tiltUntil ? this.tilt : 0;
    if (emotion === 'curious') roll += 0.14;
    if (emotion === 'happy') roll += Math.sin(t * 5) * 0.05;
    if (emotion === 'angry') roll += Math.sin(t * 22) * 0.015;
    if (chatPhase === 'thinking') roll += 0.16 + Math.sin(t * 1.3) * 0.04; // "hmm…" head tilt
    if (chatPhase === 'speaking') roll += Math.sin(t * 3.2) * 0.04;

    let pitch = -gaze.y * 0.32;
    if (chatPhase === 'speaking') pitch += Math.sin(t * 9) * 0.035; // little nods while talking
    if (sleeping) pitch += 0.22 + Math.sin(t * 0.6) * 0.04;
    if (emotion === 'sad') pitch += 0.14;
    if (emotion === 'angry') pitch += 0.08;

    const head = { yaw: gaze.x * 0.5, pitch, roll };

    // --- Body drift ---
    if (t > this.nextWanderAt) {
      this.wander = { x: rand(-0.35, 0.35), y: rand(-0.18, 0.18) };
      this.nextWanderAt = t + rand(3.5, 7.5);
    }
    const body = input.dragging
      ? { x: 0, y: 0, z: 0.2 }
      : {
          x: active || hovering ? pointer.x * 0.28 + this.wander.x * 0.3 : this.wander.x,
          y: sleeping ? -0.2 : active || hovering ? pointer.y * 0.16 : this.wander.y,
          z: hovering ? 0.35 : emotion === 'surprised' ? -0.25 : emotion === 'angry' ? 0.15 : 0,
        };

    // In conversation: stay put, shift a touch left to make room for the bubbles,
    // lean in when talking and sit back when thinking
    if (chatting && !input.dragging) {
      body.x = body.x * 0.4 - 0.12;
      body.y *= 0.5;
      body.z = chatPhase === 'speaking' ? 0.18 : chatPhase === 'thinking' ? -0.1 : 0.05;
    }

    const hop = this.pendingHop;
    this.pendingHop = 0;

    // Tuck the side plates in: when asleep, while being spun, when startled,
    // and now and then on its own like a stretch.
    if (!sleeping && !input.dragging && !chatting && t > this.nextTuckAt) {
      this.tuckUntil = t + rand(1.8, 3.2);
      this.nextTuckAt = t + rand(12, 22);
    }
    // Curls in to concentrate while thinking, opens up again to talk
    const armsClosed =
      sleeping || input.dragging || emotion === 'surprised' || chatPhase === 'thinking' || t < this.tuckUntil
        ? 1
        : 0;

    return {
      emotion,
      gaze,
      head,
      body,
      bobAmp: sleeping ? 0.04 : hovering ? 0.05 : 0.08,
      bobSpeed: sleeping ? 0.5 : chatPhase === 'thinking' ? 0.6 : chatPhase === 'speaking' ? 1.4 : emotion === 'happy' ? 1.6 : 0.9,
      hop,
      armsClosed,
    };
  }
}
